import { baseApi } from '@/Redux/baseApi';
import type { ServiceRequestRow } from './ServiceCallDrawer';

/**
 * The one read the waiter station needs that no slice already offers.
 *
 * Injected here rather than added to `Redux/Slices/operationsApi.ts` because
 * `injectEndpoints` is exactly the seam for a screen that owns a read: the
 * endpoint lands on the same `baseApi`, shares its axios base query, its
 * deduplication and its `refetchOnReconnect`, and the slice keeps its own scope.
 *
 * The pass itself needs no new endpoint at all. `useListOrdersQuery` already
 * answers `GET /orders` with the item rows and `session.tableId` the pass is
 * built from, and `applyItemEvent` already patches every cached `listOrders`
 * entry from an item event — so a plate collected on a handheld repaints here
 * with no request. Adding a second orders endpoint would have been a second
 * cache entry for the same rows that nothing patches.
 */

/** `ServiceRequestQueryDto` (+ `PaginationDto`) — nothing that is not on it. */
export interface StationQueueQuery {
  /** One status or a comma-separated list; the DTO splits it. */
  status?: string;
  type?: string;
  branchId?: string;
  areaId?: string;
  tableId?: string;
  assigneeId?: string;
  page?: number;
  limit?: number;
}

function clean(args: StationQueueQuery): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(args)) {
    if (value === '' || value === null || value === undefined) continue;
    out[key] = value;
  }
  return out;
}

/** §3 envelope → rows. `/service-requests` answers a page; be tolerant anyway. */
function toRows(payload: unknown): ServiceRequestRow[] {
  if (Array.isArray(payload)) return payload as ServiceRequestRow[];
  return (payload as { data?: ServiceRequestRow[] } | null)?.data ?? [];
}

export const waiterStationApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    /**
     * `GET /service-requests` — `service_requests.view`. The call-waiter queue.
     *
     * **No `providesTags`, deliberately.** `baseApi.tagTypes` has no
     * `ServiceRequest` member and this file is not the place to add one — a tag
     * that one module provides and another invalidates has to be declared once,
     * centrally, and inventing it here would either throw at injection time or
     * create a tag nothing else can ever spell. The queue's freshness comes from
     * the two things that actually move it: `SERVICE_REQUEST_UPDATED` on the
     * branch room, and the resync after a reconnect. Both call `refetch()`.
     *
     * Live state, so 30s in memory: a call shown from a cache that outlived the
     * screen is a table a waiter walks to for nothing.
     */
    stationQueue: build.query<ServiceRequestRow[], StationQueueQuery>({
      query: (args) => ({ url: '/service-requests', params: clean(args) }),
      transformResponse: toRows,
      keepUnusedDataFor: 30,
    }),
  }),
  overrideExisting: false,
});

export const { useStationQueueQuery } = waiterStationApi;
